/**
 * The one place configured origins (and the contact form's configured email
 * addresses, at the end of this file) are parsed, normalized and validated.
 *
 * Every origin the site is configured with — SITE_URL (canonical origin and
 * the Worker's redirect target) and APP_ORIGIN (the app's sign-up/sign-in
 * links) — goes through here, whether it is read by the build
 * (src/shared/build-config.ts), the deploy preflight
 * (scripts/lib/wrangler-config.mjs) or the Worker (src/worker/index.ts), so
 * all three agree on what a value means. Consumers use the normalized origin
 * this returns, never the raw string.
 *
 * Plain JavaScript (with origins.d.mts) so the Node deploy script can import
 * it without a TypeScript step.
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Every name in the IANA Special-Use Domain Names registry
 * (https://www.iana.org/assignments/special-use-domain-names/, RFC 6761 and
 * the RFCs it lists), plus `internal` (reserved by ICANN for private use) —
 * each name and every subdomain of it. None can be a public site or a
 * deliverable mail domain. One list, used for configured origins, contact
 * addresses and visitors' email addresses alike.
 */
export const SPECIAL_USE_DOMAINS = Object.freeze([
  // Top-level names.
  'alt', // RFC 9476
  'example', // RFC 6761
  'invalid', // RFC 6761
  'local', // RFC 6762 (mDNS)
  'localhost', // RFC 6761
  'onion', // RFC 7686
  'test', // RFC 6761
  'internal', // ICANN private-use TLD (not in the IANA registry, same effect)
  // Second-level names.
  'example.com', // RFC 6761
  'example.net', // RFC 6761
  'example.org', // RFC 6761
  // .arpa names.
  '6tisch.arpa', // RFC 9031
  'eap.arpa', // IANA registry
  'eap-noob.arpa', // RFC 9140
  'home.arpa', // RFC 8375
  'ipv4only.arpa', // RFC 8880
  'resolver.arpa', // RFC 9462
  'service.arpa', // IANA registry
  // Private-address reverse zones (RFC 6761 §6.1, RFC 6762, RFC 4193).
  '10.in-addr.arpa',
  ...Array.from({ length: 16 }, (_, index) => `${16 + index}.172.in-addr.arpa`),
  '168.192.in-addr.arpa',
  '254.169.in-addr.arpa',
  '8.e.f.ip6.arpa',
  '9.e.f.ip6.arpa',
  'a.e.f.ip6.arpa',
  'b.e.f.ip6.arpa',
]);

/**
 * `.arpa` as a whole is DNS infrastructure (RFC 3172): no website or mailbox
 * lives under it, so any `.arpa` name is refused too, including special-use
 * names registered after this list was written.
 */
const INFRASTRUCTURE_TLDS = new Set(['arpa']);

/** True for special-use names, IP literals and single-label hosts. */
export function isReservedHost(hostname) {
  const host = String(hostname).toLowerCase().replace(/\.$/, '');
  // Single-label names (`localhost`, `intranet`) and the special-use TLDs themselves.
  if (!host.includes('.')) return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[')) return true;
  if (INFRASTRUCTURE_TLDS.has(host.split('.').pop())) return true;
  return SPECIAL_USE_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

export function isLocalHost(hostname) {
  return LOCAL_HOSTS.has(String(hostname).toLowerCase());
}

/**
 * Whether `hostname` (as the URL parser normalized it) is a DNS name a site
 * can live on: LDH labels (letters, digits, hyphens) of 1–63 characters that
 * neither start nor end with a hyphen, no empty label (`a..b`, a trailing
 * root dot), and at most 253 characters in all. Internationalized names
 * arrive here as their `xn--` form, which is LDH. The local development hosts
 * (`localhost`, `127.0.0.1`, `[::1]`) are allowed so `local` builds work;
 * whether a host is public is decided separately (isReservedHost).
 */
export function isValidHostname(hostname) {
  const host = String(hostname ?? '').toLowerCase();
  if (LOCAL_HOSTS.has(host)) return true;
  if (host.length === 0 || host.length > 253) return false;
  return host.split('.').every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label));
}

/**
 * The raw shape of a bare origin, checked before URL parsing so nothing is
 * silently normalized away: a scheme, `://`, and an authority with no path,
 * query (`?`, even empty), fragment (`#`, even empty), credentials (`@`),
 * backslash or whitespace. The URL parser would otherwise accept and drop
 * `https://x.dev?`, `https://x.dev#`, `https://@x.dev` or `https://x.dev/.`.
 */
const BARE_ORIGIN = /^https?:\/\/[^/?#@\\\s]+$/i;

/**
 * Parses a bare origin (`scheme://host[:port]`), tolerating surrounding
 * whitespace and letter case (both are normalized away). Returns null for
 * anything that is not a bare http(s) origin on a valid host name: a path,
 * query, fragment, trailing slash or credentials is rejected rather than
 * silently trimmed, and so is a host with an empty, over-long or non-LDH
 * label (isValidHostname). Callers compare the returned `origin`
 * (scheme + host + non-default port), never the raw string.
 *
 * @returns {{ origin: string, protocol: 'https:' | 'http:', hostname: string, local: boolean } | null}
 */
export function parseOrigin(value) {
  const raw = String(value ?? '').trim();
  if (!BARE_ORIGIN.test(raw)) return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password || url.search || url.hash || (url.pathname !== '/' && url.pathname !== '')) {
    return null;
  }
  if (!isValidHostname(url.hostname)) return null;
  return { origin: url.origin, protocol: url.protocol, hostname: url.hostname, local: isLocalHost(url.hostname) };
}

/**
 * Validates and normalizes a configured origin, throwing a message a person
 * can act on.
 *
 * - `public`: a deployed environment. Must be https on a real, public host
 *   (no localhost, IANA reserved names, IP literals or single-label hosts).
 * - `local`: local development. https anywhere, or http on localhost.
 *
 * `allowReservedHostsForTests` is a test seam only (reserved `.test` hosts in
 * unit tests); production code never passes it.
 *
 * @param {string} name
 * @param {string} value
 * @param {{ context: 'public' | 'local', allowReservedHostsForTests?: boolean }} options
 * @returns {string} the normalized origin, e.g. https://www.example.org → never ends with a slash
 */
export function validateOrigin(name, value, { context, allowReservedHostsForTests = false }) {
  const raw = String(value ?? '').trim();
  const parsed = parseOrigin(raw);
  if (!parsed) {
    throw new Error(
      `${name} must be a bare origin such as https://<your domain>, with no path, query or trailing slash (got "${raw}")`,
    );
  }
  if (context === 'local') {
    if (parsed.protocol === 'http:' && !parsed.local) {
      throw new Error(`${name} must use https:// (http:// is only allowed for localhost), got "${raw}"`);
    }
    return parsed.origin;
  }
  if (parsed.protocol !== 'https:') {
    throw new Error(`${name} must use https:// for a deployed environment, got "${raw}"`);
  }
  if (parsed.local || (isReservedHost(parsed.hostname) && !allowReservedHostsForTests)) {
    throw new Error(
      `${name} "${raw}" is a placeholder, local, reserved (special-use) or IP host, not a public domain. Use the approved domain (see wrangler.jsonc).`,
    );
  }
  return parsed.origin;
}

/**
 * Configured email addresses: CONTACT_TO (the team inbox) and CONTACT_FROM
 * (the sender on the Email Service domain). Read by the Worker, which only
 * enables the contact form when both parse, and by the deploy preflight,
 * which warns when they do not — so both agree on what "configured" means.
 *
 * A plain address only (`team@landmate.app`): no display name, no list, no
 * comments. The domain must be a hostname with at least two labels and an
 * alphabetic top-level label.
 */
const EMAIL_LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** True for a hostname that could be a mail domain: LDH labels, two or more, alphabetic TLD. */
export function isMailDomain(domain) {
  const host = String(domain ?? '').toLowerCase();
  if (host.length === 0 || host.length > 253) return false;
  const labels = host.split('.');
  if (labels.length < 2 || !labels.every((label) => DOMAIN_LABEL.test(label))) return false;
  return /^[a-z]{2,63}$/.test(labels[labels.length - 1]);
}

/**
 * Parses a configured email address, tolerating surrounding whitespace and
 * domain letter case. Returns the normalized address, or null.
 *
 * @returns {string | null}
 */
export function parseEmailAddress(value) {
  const raw = String(value ?? '').trim();
  if (raw.length === 0 || raw.length > 254) return null;
  const at = raw.lastIndexOf('@');
  if (at <= 0 || raw.indexOf('@') !== at) return null;
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1).toLowerCase();
  if (local.length > 64 || !EMAIL_LOCAL_PART.test(local) || !isMailDomain(domain)) return null;
  return `${local}@${domain}`;
}

/**
 * The contact form's address configuration: the normalized addresses when
 * both parse, and a list of problems a person can act on otherwise. Unset
 * (both empty) is reported as `unset`, not as a problem: it is the expected
 * state until the owner chooses the addresses.
 *
 * An address on a reserved, special-use or single-label domain (`.test`,
 * `example.com`, `localhost`…) can never send or receive mail, so it is a
 * problem too — except where nothing is really delivered:
 * `allowReservedDomains` is for local development (wrangler dev simulates
 * the send_email binding) and the test suite (a mocked binding).
 *
 * @param {{ CONTACT_TO?: string, CONTACT_FROM?: string }} vars
 * @param {{ allowReservedDomains?: boolean }} [options]
 * @returns {{ to: string, from: string, problems: [], unset: false } | { to: null, from: null, problems: string[], unset: boolean }}
 */
export function contactAddresses(vars, { allowReservedDomains = false } = {}) {
  const rawTo = String(vars?.CONTACT_TO ?? '').trim();
  const rawFrom = String(vars?.CONTACT_FROM ?? '').trim();
  if (!rawTo && !rawFrom) return { to: null, from: null, problems: [], unset: true };
  const to = parseEmailAddress(rawTo);
  const from = parseEmailAddress(rawFrom);
  const reserved = (address) => !allowReservedDomains && isReservedHost(address.slice(address.lastIndexOf('@') + 1));
  const problems = [];
  if (!rawTo) problems.push('CONTACT_TO is not set');
  else if (!to) problems.push(`CONTACT_TO "${rawTo}" is not a plain email address (name@domain.tld)`);
  else if (reserved(to))
    problems.push(`CONTACT_TO "${to}" is on a reserved (special-use) domain, which cannot receive mail`);
  if (!rawFrom) problems.push('CONTACT_FROM is not set');
  else if (!from) {
    problems.push(`CONTACT_FROM "${rawFrom}" is not a plain email address on a sending domain (name@domain.tld)`);
  } else if (reserved(from)) {
    problems.push(`CONTACT_FROM "${from}" is on a reserved (special-use) domain, which cannot send mail`);
  }
  if (to && from && problems.length === 0) return { to, from, problems: [], unset: false };
  return { to: null, from: null, problems, unset: false };
}

/**
 * APP_LINKS_ENABLED: whether the site's "Get started" / "Sign in" links may
 * point at APP_ORIGIN. "true" / "false" (any case, trimmed), or empty for the
 * environment's default: off in production until sign-up opens to the public,
 * on in preview and local. Anything else throws: it is a configuration
 * mistake. Used by the build (src/shared/build-config.ts) and the deploy
 * preflight, so both read the value the same way.
 *
 * @param {string | null | undefined} value
 * @param {string} appEnv
 * @returns {boolean}
 */
export function parseAppLinksEnabled(value, appEnv) {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  if (raw === '') return appEnv !== 'production';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  throw new Error(`APP_LINKS_ENABLED must be "true", "false" or empty (got "${value}")`);
}
